import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import { useTheme } from '@mui/material';
import { Editor } from '@tiptap/react';
import { memo, useCallback, useState } from 'react';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { InsertItemLayout } from '../InsertItemLayout/InsertItemLayout';
import { bloxValues } from 'themes/constants';
import { JSONContent } from '@tiptap/core';

type PlainTextPasteProps = {
  editor: Editor | null;
  disabled?: boolean;
};

// Win (\r\n), Unix (\n), Mac (\r), etc newlines
const NEWLINE_REGEX = /\r\n|\r|\n/;

/**
 * Widget Select (plus menu) MenuItem insert clipboard as plain text.
 * https://developer.mozilla.org/en-US/docs/Web/API/Clipboard
 * https://w3c.github.io/clipboard-apis/#integration-with-rich-text-editing
 */
export const PlainTextPaste = memo(({ editor, disabled }: PlainTextPasteProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const notify = useNotify();

  // waiting state since clipboard API is async
  // as user may need to give browser perms first
  const [isPasting, setIsPasting] = useState(false);
  const isDisabled = disabled || isPasting;

  // Paste as Plain Text
  const uiLabel = t('blox.editorial.toolbar.widgetselect.plaintext.label');
  const uiDescription = t('blox.editorial.toolbar.widgetselect.plaintext.description');
  const uiEmptyClipboard = t('blox.editorial.toolbar.widgetselect.plaintext.empty');
  const uiErrorGeneric = t('blox.editorial.toolbar.widgetselect.plaintext.error');

  /**
   * Handle Plain Text Paste via Clipboard API
   * Clipboard API error can be a DOMException
   * NotAllowedError or NotFoundError
   * NotAllowedError: User denied permission to access clipboard
   * NotFoundError: Clipboard API failed to convert the content to text
   * It is async due to browser perms so we need to handle it with try/catch
   *
   * Note the readText() method is only supported in secure contexts (HTTPS)
   * and will return one large string of text, so we will need to format it
   * eg: split by newlines into paragraphs
   */
  const handlePlainText = useCallback(async () => {
    try {
      if (!editor) {
        console.warn('[handlePlainText] No editor instance.');
        return;
      }
      setIsPasting(true);
      // get textual representation via the clipboard api (one large string)
      const rawText = await navigator?.clipboard?.readText();

      // tokenize raw text
      const tokens: string[] = [];
      const split = rawText?.split(NEWLINE_REGEX);

      // trim and push to tokens
      for (const part of split) {
        const trimmed = part.trim();
        if (trimmed) {
          tokens.push(trimmed);
        }
      }

      // notify and return if no tokens
      if (!tokens.length) {
        notify(uiEmptyClipboard, { type: 'info' });
        return;
      }

      // build tiptap JSONContent <p> text paragraphs per token
      const jscFormatPaste: JSONContent[] = tokens.map((t) => ({
        type: 'paragraph',
        content: [{ type: 'text', text: t }],
      }));

      // insert the clean text into the editor
      const success = editor?.chain()?.focus()?.insertContent(jscFormatPaste)?.run();

      // notify error and return if failed
      if (!success) {
        console.error('[handlePlainText] Failed to insert content into editor.');
        notify(uiErrorGeneric, { type: 'error' });
        return;
      }
    } catch (err) {
      console.error('[handlePlainText] Error:', err);
      if (err instanceof DOMException) {
        notify(err?.message, { type: 'error' });
        return;
      }
      // fallback for any other error
      notify(uiErrorGeneric, { type: 'error' });
    } finally {
      // reset waiting state
      setIsPasting(false);
    }
  }, [editor, notify, uiEmptyClipboard, uiErrorGeneric]);

  return (
    <InsertItemLayout
      id="widget-select-plain-text-paste-option"
      title={uiLabel}
      description={uiDescription}
      action={handlePlainText}
      disabled={isDisabled}
      icon={
        <ContentPasteGoIcon
          sx={{
            color: theme.palette.text.secondary,
            fontSize: bloxValues.iconSizeRegular,
          }}
        />
      }
    />
  );
});
PlainTextPaste.displayName = 'PlainTextPaste';
